import type {
  AnnotationImageRenderResource,
  ReplayableRenderSourceDescriptor,
} from "@carbon6/c6-frontend-design/annotation";

export type AnnotationImageSize = Readonly<{ width: number; height: number }>;

type ObjectUrlFactory = Readonly<{
  createObjectURL(blob: Blob): string;
  revokeObjectURL(url: string): void;
}>;

const defaultObjectUrls: ObjectUrlFactory = {
  createObjectURL: (blob) => URL.createObjectURL(blob),
  revokeObjectURL: (url) => URL.revokeObjectURL(url),
};

export function createAnnotationFrameDescriptor(
  blob: Blob,
  options: {
    id: string;
    revision?: number;
    objectUrls?: ObjectUrlFactory;
  },
): ReplayableRenderSourceDescriptor<AnnotationImageRenderResource> {
  const objectUrls = options.objectUrls ?? defaultObjectUrls;
  let acquisition = 0;

  return {
    id: options.id,
    revision: options.revision ?? 1,
    async acquire({ signal }) {
      if (signal.aborted) throw signal.reason;
      const url = objectUrls.createObjectURL(blob);
      let disposed = false;
      if (signal.aborted) {
        objectUrls.revokeObjectURL(url);
        throw signal.reason;
      }
      acquisition += 1;
      return {
        id: `${options.id}:resource:${acquisition}`,
        image: url,
        dispose() {
          if (disposed) return;
          disposed = true;
          objectUrls.revokeObjectURL(url);
        },
      };
    },
  };
}

export async function decodeAnnotationImageSize(
  blob: Blob,
  options: {
    signal: AbortSignal;
    createBitmap?: (blob: Blob) => Promise<ImageBitmap>;
  },
): Promise<AnnotationImageSize> {
  if (options.signal.aborted) throw options.signal.reason;
  const createBitmap = options.createBitmap ?? createImageBitmap;
  const bitmap = await createBitmap(blob);
  try {
    if (options.signal.aborted) throw options.signal.reason;
    return { width: bitmap.width, height: bitmap.height };
  } finally {
    bitmap.close();
  }
}
